import { Check, Lock, Sparkles } from "lucide-react";
import { APPS } from "@/data/apps";
import { useHive } from "@/store/useHive";
import { productState, PRODUCT_PREVIEW } from "@/lib/entitlements";
import { Modal } from "@/components/ui/Modal";
import type { ProductId } from "@/api/types";

/** "Request early access" CTA with requested / team-requested states. */
export function EarlyAccessButton({ product, accent }: { product: ProductId; accent: string }) {
  const requestEarlyAccess = useHive((s) => s.requestEarlyAccess);
  const requestedAt = useHive((s) => s.earlyAccessMine[product]);
  const orgCount = useHive((s) => s.earlyAccessOrg[product] ?? 0);
  const teammates = orgCount - (requestedAt ? 1 : 0);

  return (
    <div className="flex flex-col items-center gap-1.5">
      {requestedAt ? (
        <div className="flex items-center gap-2 rounded-lg border border-app px-3.5 py-2 text-xs font-semibold text-app">
          <Check size={13} className="text-emerald-400" /> You're on the list — we'll reach out
        </div>
      ) : (
        <button
          onClick={() => void requestEarlyAccess(product)}
          className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold text-black transition hover:brightness-105"
          style={{ background: accent }}
        >
          <Sparkles size={13} /> Request early access
        </button>
      )}
      {teammates > 0 && (
        <span className="text-[10px] text-app-2">
          Also requested by {teammates} teammate{teammates === 1 ? "" : "s"}
        </span>
      )}
    </div>
  );
}

/** Capability blurb + demand-capture CTA — shared by the locked-tile modal and
 *  the preview window panel. Branding stays full-strength; the state chip and
 *  CTA make the gating obvious. */
export function ProductPreviewContent({ product }: { product: ProductId }) {
  const app = APPS[product];
  const copy = PRODUCT_PREVIEW[product];
  const state = useHive((s) => productState(s.entitlements, product));

  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <div
        className="grid h-14 w-14 place-items-center rounded-2xl text-white shadow-lg"
        style={{ background: `linear-gradient(135deg, ${app.accent}, ${app.accent}aa)` }}
      >
        <app.icon size={26} />
      </div>
      <div>
        <div className="flex items-center justify-center gap-2">
          <span className="text-base font-semibold text-app">{app.name}</span>
          <span
            className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
            style={{ background: `${app.accent}22`, color: app.accent }}
          >
            {state === "locked" ? <Lock size={9} /> : <Sparkles size={9} />} Early Access
          </span>
        </div>
        <p className="mt-2 max-w-md text-xs leading-relaxed text-app-1">{copy.headline}</p>
      </div>
      <ul className="w-full max-w-md space-y-1.5 text-left">
        {copy.capabilities.map((cap) => (
          <li key={cap} className="flex items-start gap-2 text-xs leading-relaxed text-app-1">
            <Check size={13} className="mt-0.5 shrink-0" style={{ color: app.accent }} />
            {cap}
          </li>
        ))}
      </ul>
      <EarlyAccessButton product={product} accent={app.accent} />
    </div>
  );
}

/** Desktop-level modal for locked (and dock-previewed) products. Mounted once in
 *  App; the z-[95] wrapper keeps it above windows (whose z grows per focus). */
export function ProductPreviewModal() {
  const previewProduct = useHive((s) => s.previewProduct);
  const openProductPreview = useHive((s) => s.openProductPreview);
  if (!previewProduct) return null;
  const app = APPS[previewProduct];

  return (
    <div className="absolute inset-0 z-[95]">
      <Modal
        open
        onClose={() => openProductPreview(null)}
        title={`${app.name} — Early Access`}
        subtitle={app.tagline}
        accent={app.accent}
        width={520}
      >
        <ProductPreviewContent product={previewProduct} />
      </Modal>
    </div>
  );
}
